import pino, { type Logger, LoggerOptions, stdTimeFunctions } from "pino";

import { IS_LOCALHOST } from "~/env";

const initializeLogger = (): Logger => {
  const isTest = process.env.NODE_ENV === "test";
  const isNextRuntime = process.env.NEXT_RUNTIME != null;
  const useWorkerTransport = IS_LOCALHOST && !isTest && !isNextRuntime;
  const useSyncLocalLogs = IS_LOCALHOST && !isTest && isNextRuntime;

  const loggerOptions: LoggerOptions = {
    browser: {},
    level: process.env.LOG_LEVEL ?? (process.env.NODE_ENV === "production" ? "info" : "debug"),
    timestamp: stdTimeFunctions.isoTime,
    base: {
      revision: process.env.GIT_COMMIT_SHA,
      // Every log line carries its environment independently of infra tagging. The ECS
      // firelens `dd_tags` also set `env:<env>`, but only the Next.js process gets
      // dd-trace log injection (ecosystem.config.js `--require ./start-init`), so this is
      // the one env marker the worker process emits itself. Undefined off ECS, exactly
      // like `revision` — pino drops undefined base fields.
      env: process.env.DD_ENV,
    },
    serializers: {
      err: pino.stdSerializers.err,
      error: pino.stdSerializers.err,
      cause: pino.stdSerializers.err,
    },
  };

  // Only add custom formatters when NOT using transport (transport doesn't allow them)
  if (!useWorkerTransport && !useSyncLocalLogs) {
    loggerOptions.formatters = {
      level: level => ({ level }),
    };
  }

  // Next dev/instrumentation webpack-bundles pino transport workers (thread-stream),
  // which breaks worker.js resolution. Plain stdout JSON avoids worker + pino-pretty bundles.
  if (useSyncLocalLogs) {
    return pino({
      ...loggerOptions,
      formatters: {
        level: level => ({ level }),
      },
    });
  }

  if (useWorkerTransport) {
    loggerOptions.transport = {
      targets: [
        {
          target: "pino-pretty",
          level: "debug",
          options: {},
        },
        {
          target: "pino/file",
          level: "debug",
          options: {
            destination: ".logs/app.log",
            mkdir: true,
          },
        },
      ],
    };
  }

  return pino(loggerOptions);
};

export const logger: Logger = initializeLogger();
